// The desktop right-pane default state (web/plan.md §Layout concept): what the
// AppShell mounts for `/` and `/contacts` when no contact is selected. A fresh
// install must not be a dead end, so it surfaces the user's own identity — the
// mercury avatar, a "select a contact" line, and a copyable ID chip with a QR
// shortcut (the same add-link QR the Add-friend screen shows). Desktop-only:
// the compact UI keeps its tab bar and never renders this.

import { useState, type ReactNode } from 'react';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { useT } from '../../i18n/index.js';
import { useAccount } from '../../shell/accountContext.js';
import { SHARE_ORIGIN } from '../../shell/config.js';
import { QrCode } from '../qr/QrCode.js';

/** First 1–2 initials of a display name (Latin or Cyrillic), for the disc. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** Flat initials disc — the avatar fallback when the account has no seed yet. */
function InitialsDisc({ name, size }: { name: string; size: number }): ReactNode {
  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center rounded-full bg-raised font-semibold text-ink"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initialsOf(name)}
    </div>
  );
}

const AVATAR_SIZE = 72;

export function EmptyPane(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  const [showQr, setShowQr] = useState(false);

  const account = state.status === 'ready' ? state.account : undefined;
  const userId = account?.userId;
  const avatarSeed = account?.avatarSeed;
  // Same link the Add-friend screen shares: the UUID rides in the #fragment and
  // never hits the network; without a public origin, the bare UUID is the code.
  const shareValue = userId ? (SHARE_ORIGIN ? `${SHARE_ORIGIN}/add#${userId}` : userId) : null;

  return (
    <main className="flex min-h-full flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      {userId !== undefined && avatarSeed !== undefined ? (
        <MercuryAvatar
          seed={candidateSeed(userId, avatarSeed)}
          size={AVATAR_SIZE}
          className="rounded-full"
        />
      ) : (
        <InitialsDisc name={account?.displayName ?? ''} size={AVATAR_SIZE} />
      )}

      <div>
        <p className="text-base font-medium text-ink">{t('emptyPane.brand')}</p>
        <p className="mt-1 text-sm text-muted">{t('emptyPane.selectContact')}</p>
      </div>

      {userId && (
        <div className="flex flex-col items-center gap-3">
          <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-edge bg-surface px-4 py-2 text-xs text-muted">
            <span>{t('emptyPane.yourId')}</span>
            <span data-testid="empty-pane-uuid" className="select-text break-all font-mono text-ink">
              {userId}
            </span>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              aria-expanded={showQr}
              onClick={() => setShowQr((v) => !v)}
              className="font-medium text-accent"
            >
              {showQr ? t('emptyPane.hideQr') : t('emptyPane.showQr')}
            </button>
          </div>
          {showQr && shareValue && <QrCode value={shareValue} size={200} />}
        </div>
      )}
    </main>
  );
}
